import { Node, mergeAttributes } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { EditorView } from '@tiptap/pm/view';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    details: {
      /**
       * Toggle a details wrapper
       */
      toggleDetails: () => ReturnType;
      /**
       * Set details wrapper
       */
      setDetails: () => ReturnType;
      /**
       * Unset details wrapper
       */
      unsetDetails: () => ReturnType;
    };
  }
}

export interface DetailsOptions {
  HTMLAttributes: Record<string, any>;
}

/**
 * NodeView for handling Details element interactive behavior
 */
class DetailsNodeView {
  dom: HTMLDetailsElement;
  contentDOM: HTMLElement;
  node: ProseMirrorNode;
  view: EditorView;
  getPos: () => number;

  constructor(node: ProseMirrorNode, view: EditorView, getPos: () => number) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;

    // Create the details element
    this.dom = document.createElement('details');
    this.contentDOM = this.dom;

    // Set initial open state
    this.syncOpenState();

    // Handle clicks on summary elements
    this.dom.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const summary = target.closest('summary');
    if (!summary || !this.dom.contains(summary)) return;

    // Prevent default HTML behavior - we want TipTap to control state
    event.preventDefault();
    
    // Update TipTap state
    const pos = this.getPos();
    const newAttrs = { ...this.node.attrs, open: !this.node.attrs.open };
    
    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, newAttrs)
    );
  }

  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.syncOpenState();
    return true;
  }

  syncOpenState() {
    if (this.node.attrs.open) {
      this.dom.setAttribute('open', '');
    } else {
      this.dom.removeAttribute('open');
    }
  }

  destroy() {
    this.dom.removeEventListener('click', this.handleClick);
  }
}

export const Details = Node.create<DetailsOptions>({
  name: 'details',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'detailsSummary detailsContent',

  defining: true,

  addAttributes() {
    return {
      open: {
        default: false,
        parseHTML: element => element.hasAttribute('open'),
        renderHTML: attributes => {
          if (attributes.open) {
            return { open: '' };
          }
          return {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'details',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['details', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addNodeView() {
    return (props) => new DetailsNodeView(props.node, props.view, props.getPos);
  },

  addCommands() {
    return {
      toggleDetails:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name);
        },
      setDetails:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name);
        },
      unsetDetails:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});

export const DetailsSummary = Node.create({
  name: 'detailsSummary',

  group: 'block',

  content: 'inline*',

  defining: true,

  parseHTML() {
    return [
      {
        tag: 'summary',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['summary', mergeAttributes(HTMLAttributes), 0];
  },
});

export const DetailsContent = Node.create({
  name: 'detailsContent',

  group: 'block',

  content: 'block+',

  defining: true,

  parseHTML() {
    return [
      {
        tag: 'div[data-type="details-content"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'details-content' }, HTMLAttributes), 0];
  },
});
